import { Vue, Component, Prop, InjectReactive, Inject, Watch } from 'vue-property-decorator';
import { Step, StepPager, Btn, Toolbar, Field, DropdownField, IStepComponent, IRawMetaData, IFileSource, EventBus } from '@samc/centricity-core';
import { IImportFile, ITextValuePair } from '@/Interfaces';
import { ITextValue, AjaxHelper } from '@samc/common';
import { VuePlugin } from 'vuera';
import { ScratchCompare } from '@samc/querybuilder/core/ClientApp/modular/Scratchpad/CompareTool/ScratchCompare';
import ComparisionFlow from '@samc/querybuilder/core/ClientApp/modular/Scratchpad/model/ComparisionFlow';
import { ConfigurationContextProps, QueryProps } from '@samc/querybuilder';
import { download } from '@/utils';
import { ImportedFilesSource } from '@/Sources';
import { format } from 'date-fns';
import '@/registerVuePlugins';

Vue.use(VuePlugin);

@Component({
  name: 'source-compare-step',
  components: {
    Step, StepPager, Btn, Toolbar, Field, DropdownField, Compare: (ScratchCompare as any)
  }
})
export default class SourceCompareComponent extends Vue implements IStepComponent {
  public description: string = '';
  public isDirty: boolean = false;
  public isReadonly: boolean = false;
  public importedFileSource: ImportedFilesSource | IImportFile[] = [];

  public previousSource = '';
  public previousKey = '';
  public currentSource = '';
  public currentKey = '';
  public compareKey = 0;
  public jwt: string | null = null;
  public compareUploadedFiles: boolean = true;
  public comparisionFlow: ComparisionFlow = ComparisionFlow.Standalone;

  @Prop() public url!: string;
  @Prop() public verificationToken!: string;
  @Prop() public packageId!: string;
  @Prop({ required: true }) public scratchPadUrl!: string;
  @Prop({ default: false }) public optional!: boolean;
  @Prop({ default: 'Compare' }) public title!: string;

  @Inject({ from: 'eventBus', default: () => null }) public eventBus!: EventBus | null;

  @InjectReactive() public basicMetaData!: IRawMetaData | null;
  @InjectReactive() public fileSource!: IFileSource | null;

  public get lsProps(): QueryProps & ConfigurationContextProps {
    return {
      antiforgeryToken: this.verificationToken,
    }
  }

  /**
   * The wrapper for the component instance
   */
  public get component(): IStepComponent | null {
    return {
      isValid: this.isValid,
      isDirty: this.isDirty,
      isReadonly: this.isReadonly,
      clear: this.clear,
      initializeEvents: this.initializeEvents,
      clearEvents: this.clearEvents,
      initialize: this.initialize,
      enter: this.enter,
      skip: this.skip,
      leave: this.leave
    };
  };

  public get leftDisplayName(): string { return this.fileSource?.name ?? ''; }
  public get rightDisplayName(): string {
    const source = this.files.find(x => x.rawDataDocId === this.previousSource);
    return source?.fileName ?? '';
  }
  public get isValid(): boolean { return this.previousSource.length > 0 && this.previousKey.length > 0 && this.currentSource.length > 0 && this.currentKey.length > 0; }

  public get helper(): AjaxHelper { return new AjaxHelper(this.url, this.verificationToken, null, null); }
  public get previousSourceKeys(): ITextValue<string>[] {
    const source = this.files.find(x => x.rawDataDocId === this.previousSource);
    if (source === undefined) return [];
    if (source.headers) return source.headers.map(x => ({ text: x, value: x }));
    return [{ text: source.sourceColumnName, value: source.sourceColumnName }];
  }
  public get currentSourceKeys() {
    if (this.basicMetaData === null) return [];
    return this.basicMetaData.columnMeta.concat().sort((a, b) => a.uniqueCount > b.uniqueCount ? -1 : 1);
  }
  public get previousFileSource(): ITextValuePair<string>[] {
    return this.files
      .map(x => ({ text: `${x.fileName} (${this.formatDate(new Date(x.importCompletedDate))})`, value: x.rawDataDocId }));
  }
  public get files(): IImportFile[] {
    if (Array.isArray(this.importedFileSource)) return [];
    return this.importedFileSource.files;
  }

  public get postData() {
    if (this.currentSource === null || this.previousSource === null) return null;
    return {
      Left: {
        Key: `[${this.currentKey}]`,
        ExtRefSpec: {
          ExtRefAlias: 'RawDataImport',
          ExtRefRelativeURL: this.currentSource,
          ExtRefFileAlias: this.leftDisplayName
        }
      },
      Right: {
        Key: `[${this.previousKey}]`,
        ExtRefSpec: {
          ExtRefAlias: 'RawDataImport',
          ExtRefRelativeURL: this.previousSource,
          ExtRefFileAlias: this.rightDisplayName
        }
      },
      ComparisonFlow: 5
    };
  }

  /**
     * Will parse the scratchpad url given and add a / if one does not exist on the end
     */
  public get parsedScratchPadUrl(): string {
    const url = this.scratchPadUrl ?? '';
    if (url.endsWith('/')) return url;
    return url + '/api/';
  }

  public get previousSourceName(): string {
    const item = this.previousFileSource.find(x => x.value === this.previousSource);
    return item?.text ?? '';
  }

  public async mounted() {
    this.verificationToken ??= await this.$verificationToken();
  }

  public async clear(): Promise<void> {
    this.description = '';
    this.isDirty = false;
    this.isReadonly = false;
    this.importedFileSource = [];

    this.previousSource = '';
    this.previousKey = '';
    this.currentSource = '';
    this.currentKey = '';
    this.compareKey = 0;
  }

  public async initialize(): Promise<boolean> {
    this.importedFileSource = new ImportedFilesSource(this.url);
    this.importedFileSource.setPackageId(this.packageId);
    return true;
  }

  public async enter(): Promise<void> {
    // On Enter Event
  }

  public async skip(): Promise<void> {
    // On Skip
  }

  /**
   * Intialize events for the event bus
   **/
  public async initializeEvents(): Promise<void> {
    // Do Nothing
  }

  /**
   * Remove events from the event bus
   **/
  public async clearEvents(): Promise<void> {
    // Do Nothing
  }

  public async leave(dir: 1 | -1): Promise<boolean> {
    return true;
  }

  public async gotoSurvivorship() {
    if (await this.$confirm.success('Do you want to leave the page?')) {
      window.location.href = '#'; // TODO: Add this when survivorship UI is created
    }
  }

  /**
   * Callback method to be used when exporting the compared data
   **/
  public async onExport(options: any) {
    try {
      const helper = new AjaxHelper(this.scratchPadUrl ?? '', this.verificationToken, null, this.$token);
      const data = await helper.postData<any>('/api/ScratchpadExtRef/CompareAndExport', { ...this.postData, options }, {}, { responseType: 'blob' });
      download(data, 'Compare.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    } catch (ex: any) {
      if (ex.isAxiosError) {
        const data = ex.response.data;
        if (data?.DeveloperMessage?.Message.indexOf('404') > -1) {
          this.$alert.error('Raw data for file could not be found!');
        } else {
          this.$alert.error('A scratchpad error ocurred!');
        }
      } else {
        this.$alert.error('An error occurred!');
      }
    }
  }

  @Watch('$token', { immediate: true })
  public onTokenChanged() {
    this.jwt = this.$token;
  }

  @Watch('previousSource')
  public onPreviousSourceUpdated(newPreviousSource: string) {
    const source = this.files.find(x => x.rawDataDocId === newPreviousSource);
    if (source !== undefined) {
      if (source.headers !== null && source.headers.length > 0) {
        this.previousKey = source.headers[0];
      } else if (source.sourceColumnName) {
        this.previousKey = source.sourceColumnName;
      }
    }
  }

  @Watch('previousSource')
  @Watch('previousKey')
  @Watch('currentSource')
  @Watch('currentKey')
  public refreshCompareTool() {
    this.compareKey += 1;
  }

  public formatDate(date: Date): string {
    let formattedDate: string = '';
    if (date) {
      formattedDate = format(date, 'MM/dd/yyyy hh:mm tt');
    }
    return formattedDate;
  }

  @Watch('packageId')
  public async onPackageIdUpdated(newPackageId: string) {
    this.previousKey = '';
    this.previousSource = '';
    if (!Array.isArray(this.importedFileSource)) {
      this.importedFileSource.setPackageId(newPackageId);
    }
  }
}